import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Navbar from "../Navbar";
import TreatImage from "./TreatImage";
import { getTreatBlog } from "../../services/treatApi";
import "../Pro/Pro.css";
import "./Treat.css";

// =====================================================
// CHOOSE A TREAT  (Step 3)  — /buy-treat/:blogId
//
// One treat per transaction: choosing another replaces the selection. Prices
// and images come from the server's database. The optional greeting is 50
// letters at most, with the conduct warning shown right under it.
// =====================================================

function TreatSelect() {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = location.state || {};

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(back.treat?._id || "");
  const [message, setMessage] = useState(back.message || "");

  useEffect(() => {
    let cancelled = false;
    getTreatBlog(blogId)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(err?.response?.data?.message || "Could not load treats."));
    return () => {
      cancelled = true;
    };
  }, [blogId]);

  const max = data?.messageMax || 50;
  const chosen = data?.treats?.find((t) => String(t._id) === String(selected));

  const next = () => {
    if (!chosen) return;
    navigate(`/buy-treat/${blogId}/confirm`, {
      state: { treat: chosen, message: message.trim(), blogger: data.blogger, blog: data.blog },
    });
  };

  let blocker = null;
  if (data && !data.treatsEnabled) blocker = "The blogger has not enabled treats on this post.";
  else if (data && data.isOwnBlog) blocker = "You cannot buy yourself a treat.";

  return (
    <div className="pro-page-wrap">
      <Navbar />
      <div className="pro-container pro-container-narrow">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🎁 BUY ME A TREAT</div>
          <h1 className="pro-hero-title">Choose a Treat</h1>
          {data?.blogger && (
            <p className="pro-hero-sub">
              for {data.blogger.name}
              {data.blog?.title ? ` · ${data.blog.title}` : ""}
            </p>
          )}
        </header>

        {!data && !error && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading treats…</p>
          </div>
        )}

        {(error || blocker) && (
          <div className="pro-card pro-empty">
            <p>{error || blocker}</p>
            <button type="button" className="pro-btn pro-btn-ghost" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        )}

        {data && !blocker && (
          <div className="pro-card">
            <div className="treat-grid" role="radiogroup" aria-label="Treats">
              {data.treats.map((t) => {
                const on = String(selected) === String(t._id);
                return (
                  <button
                    key={t._id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`treat-option${on ? " treat-option-on" : ""}`}
                    onClick={() => setSelected(t._id)}
                  >
                    {on && <span className="treat-selected-tag">Selected</span>}
                    <TreatImage image={t.image} name={t.name} size={64} />
                    <span className="treat-option-name">{t.name}</span>
                    <strong>Rs. {t.priceRupees}</strong>
                  </button>
                );
              })}
            </div>

            <label className="treat-label" htmlFor="treat-message">
              Greeting <span className="treat-dim">(optional)</span>
            </label>
            <textarea
              id="treat-message"
              className="treat-textarea"
              maxLength={max}
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, max))}
              placeholder="Say something nice…"
            />
            <div className="treat-count">{message.length}/{max}</div>
            <p className="treat-warning" role="note">
              ⚠ Please be respectful. Greetings that are sexually harassing, hateful or abusive will be
              reported, and the sender will be removed from the app.
            </p>

            <div className="treat-actions">
              <button type="button" className="pro-btn pro-btn-ghost" onClick={() => navigate(-1)}>
                Go Back
              </button>
              <button type="button" className="pro-btn pro-btn-primary" onClick={next} disabled={!chosen}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TreatSelect;
